import { inlineScript } from "@decocms/blocks/sdk/useScript";
import type { AnalyticsEvent } from "@decocms/apps-commerce/types";
import { useEffect } from "react";

// Single global observer + single delegated click listener.
// Replaces the previous pattern of one IntersectionObserver and one
// addEventListener per tracked element — which scaled to 83+ observers
// on the home alone and was freezing the main thread during script
// evaluation.
//
// A MutationObserver garbage-collects map entries + unobserves targets
// when elements with data-deco-view / data-deco-click are removed from
// the DOM. Without this, navigating between PDPs (same route, params
// only) leaves stale entries in viewMap/clickMap and detached elements
// pinned in the IntersectionObserver's observation list, which is what
// caused Chrome to flag the tab as "consuming background resources" and
// freeze on the 2nd or 3rd PDP load.
//
// ORDEM: o registrar NÃO roda antes dos SendEventOn*, e não dá para fazer
// rodar. Ele é `children` do `DecoRootLayout`, que os renderiza DEPOIS do
// `<main>` — então qualquer SendEventOn* eager dentro do `<main>` é parseado
// e executado antes. O comentário anterior aqui dizia que `defer` resolvia
// isso, mas `defer` é IGNORADO em script inline: a spec só aplica o atributo
// a script com `src`. Medido no HTML da PDP do candidato (15/09/2026): o
// `__decoRegisterView` do `view_item` saía no byte 67913 e o registrar no
// 88773, então a chamada caía no `&&` e o evento sumia sem erro nenhum.
//
// Por isso os SendEventOn* enfileiram em `__decoPending*` quando o registrar
// ainda não existe, e o registrar drena a fila ao inicializar. Mesmo padrão
// do `_q` de `window.DECO.events`. Resultado independe de ordem de DOM.
//
// Verificado ponta a ponta em `vite dev` (21/09/2026), PDP: a fila drena, o
// elemento recebe `data-deco-view`, o IntersectionObserver dispara e tanto
// `view_item` quanto `add_to_cart` chegam ao `dataLayer` com item e valor
// corretos. Só funciona junto com o #252 — antes dele a section eager sumia
// na hidratação e levava o alvo do observer embora.
//
// AO MEDIR ISTO: o IntersectionObserver não computa nada em aba de fundo
// (`document.visibilityState === "hidden"` ⇒ sem frames ⇒ sem callback).
// Medir com a aba em primeiro plano, senão o resultado é um falso negativo
// que parece bug do site.
export const REGISTRAR_SCRIPT = `(function () {
  if (window.__decoAnalyticsReady) return;
  window.__decoAnalyticsReady = true;

  var clickMap = window.__decoClickEvents = {};
  var viewMap = window.__decoViewEvents = {};
  var seenOnce = new Set();

  document.addEventListener("click", function (e) {
    var t = e.target instanceof Element ? e.target.closest("[data-deco-click]") : null;
    if (!t) return;
    var key = t.getAttribute("data-deco-click");
    var ev = key && clickMap[key];
    if (ev && window.DECO && window.DECO.events) {
      window.DECO.events.dispatch(ev);
    }
  }, { passive: true });

  // Despacho imediato (trigger "render"). Uma página em prerender ainda não
  // foi vista: espera ela ser ativada.
  function dispatchNow(event) {
    if (document.prerendering) {
      document.addEventListener("prerenderingchange", function () {
        dispatchNow(event);
      }, { once: true });
      return;
    }
    if (window.DECO && window.DECO.events) {
      window.DECO.events.dispatch(event);
    }
  }

  var observer = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (!entry.isIntersecting) continue;
      var el = entry.target;
      var key = el.getAttribute("data-deco-view");
      var meta = key && viewMap[key];
      if (!meta) continue;
      if (meta.sendOnce && seenOnce.has(key)) {
        observer.unobserve(el);
        continue;
      }
      if (window.DECO && window.DECO.events) {
        window.DECO.events.dispatch(meta.event);
      }
      if (meta.sendOnce) {
        seenOnce.add(key);
        observer.unobserve(el);
      }
    }
  });
  window.__decoViewObserver = observer;

  // Numa navegação SPA o React pode trocar o elemento de um id na mesma
  // commit, e o efeito do SendEventOn* registra o NOVO antes deste callback
  // (microtask) rodar. Só apaga a entrada se ela ainda for do elemento que saiu,
  // senão o registro novo morre junto com o velho.
  function cleanupElement(el) {
    if (!el || el.nodeType !== 1) return;
    var viewKey = el.getAttribute && el.getAttribute("data-deco-view");
    if (viewKey) {
      observer.unobserve(el);
      if (!viewMap[viewKey] || viewMap[viewKey].el === el) {
        delete viewMap[viewKey];
        seenOnce.delete(viewKey);
      }
    }
    var clickKey = el.getAttribute && el.getAttribute("data-deco-click");
    if (clickKey) {
      var current = document.getElementById(clickKey);
      if (!current || current === el) delete clickMap[clickKey];
    }
  }

  var mutObs = new MutationObserver(function (mutations) {
    for (var m = 0; m < mutations.length; m++) {
      var removed = mutations[m].removedNodes;
      for (var i = 0; i < removed.length; i++) {
        var node = removed[i];
        if (!node || node.nodeType !== 1) continue;
        cleanupElement(node);
        if (node.querySelectorAll) {
          var views = node.querySelectorAll("[data-deco-view]");
          for (var v = 0; v < views.length; v++) cleanupElement(views[v]);
          var clicks = node.querySelectorAll("[data-deco-click]");
          for (var c = 0; c < clicks.length; c++) cleanupElement(clicks[c]);
        }
      }
    }
  });
  mutObs.observe(document.body, { childList: true, subtree: true });

  window.__decoRegisterClick = function (id, event) {
    var el = document.getElementById(id);
    if (!el) return;
    clickMap[id] = event;
    el.setAttribute("data-deco-click", id);
  };

  // trigger "view" (padrão): despacha quando o elemento entra no viewport.
  // trigger "render": despacha assim que o elemento existe — para listas que
  // são o conteúdo principal da página (PLP/busca), onde "a lista apareceu"
  // não pode depender de o IntersectionObserver rodar (aba de fundo/ocluída).
  //
  // Mesmo elemento + mesmo payload = hidratação ou rerender: o script inline e
  // o efeito do SendEventOn* chamam isto para o mesmo nó, e só o primeiro vale.
  // Elemento novo (navegação SPA) ou payload novo (outra lista) reemite.
  window.__decoRegisterView = function (id, event, sendOnce, trigger) {
    var el = document.getElementById(id);
    if (!el) return;
    var sig = JSON.stringify(event);
    var prev = viewMap[id];
    if (prev && prev.el === el && prev.sig === sig) return;
    if (prev && prev.el && prev.el !== el) observer.unobserve(prev.el);
    viewMap[id] = { event: event, sendOnce: sendOnce, sig: sig, el: el };
    seenOnce.delete(id);
    el.setAttribute("data-deco-view", id);
    if (trigger === "render") {
      observer.unobserve(el);
      seenOnce.add(id);
      dispatchNow(event);
      return;
    }
    observer.observe(el);
  };

  // Drena o que foi registrado antes deste script existir. Aqui o parse do
  // <main> já terminou, então o getElementById dentro de cada register
  // encontra o elemento — inclusive nos casos em que ele ainda não existia
  // no momento da chamada original.
  function drain(pending, register) {
    for (var i = 0; pending && i < pending.length; i++) {
      register.apply(null, pending[i]);
    }
  }
  drain(window.__decoPendingViews, window.__decoRegisterView);
  drain(window.__decoPendingClicks, window.__decoRegisterClick);
  window.__decoPendingViews = window.__decoPendingClicks = null;
})();`;

declare global {
    interface Window {
        __decoAnalyticsReady?: boolean;
        __decoClickEvents?: Record<string, unknown>;
        __decoViewEvents?: Record<
            string,
            { event: unknown; sendOnce: boolean; sig: string; el: Element }
        >;
        __decoViewObserver?: IntersectionObserver;
        __decoRegisterClick?: (id: string, event: unknown) => void;
        __decoRegisterView?: (
            id: string,
            event: unknown,
            sendOnce: boolean,
            trigger?: ViewTrigger,
        ) => void;
        // Filas para os SendEventOn* que rodam antes do registrar — ver o
        // bloco ORDEM no topo deste arquivo.
        __decoPendingClicks?: [string, unknown][] | null;
        __decoPendingViews?: unknown[][] | null;
    }
}

type ViewTrigger = "view" | "render";

/**
 * Registra o evento agora se o registrar já existe, senão enfileira.
 *
 * Os SendEventOn* são scripts inline renderizados no meio da página; o
 * registrar sai depois do `</main>`. Sem a fila, tudo que for eager dentro do
 * `<main>` — hoje o `view_item` da PDP — é perdido silenciosamente.
 */
const registerOrQueue = (fn: string, queue: string, payload: string) =>
    `(function(a){window.${fn}?window.${fn}.apply(null,a)` +
    `:(window.${queue}=window.${queue}||[]).push(a)})(${payload})`

/**
 * O `<script>` inline só executa quando chega no HTML do servidor. Quando o
 * React cria o nó no cliente — navegação SPA (inclusive voltar/avançar),
 * section deferred, boundary renderizado no cliente — ele é inerte, e o
 * evento sumia sem erro (#281: voltar da PDP para a PLP não emitia
 * `view_item_list`, e os cards nem registravam o `select_item`).
 *
 * O efeito cobre esses casos. Na hidratação ele também roda, mas o registrar
 * descarta o registro repetido (mesmo elemento + mesmo payload). A dependência
 * é o payload serializado, então rerender com os mesmos dados não refaz nada.
 */
const useRegisterOnClient = (
    fn: "__decoRegisterView" | "__decoRegisterClick",
    queue: "__decoPendingViews" | "__decoPendingClicks",
    payload: string,
) => {
    useEffect(() => {
        const args = JSON.parse(payload) as unknown[]
        const register = window[fn] as ((...a: unknown[]) => void) | undefined
        if (register) register(...args)
        else (window[queue] ??= []).push(args as never)
    }, [fn, queue, payload])
}

export const AnalyticsRegistrar = () => (
    <script {...inlineScript(REGISTRAR_SCRIPT)} />
);

export const SendEventOnClick = <E extends AnalyticsEvent>({
    event,
    id,
}: {
    event: E
    id: string
}) => {
    const payload = JSON.stringify([id, event])
    useRegisterOnClient("__decoRegisterClick", "__decoPendingClicks", payload)
    return (
        <script
            {...inlineScript(
                registerOrQueue("__decoRegisterClick", "__decoPendingClicks", payload),
            )}
        />
    )
}

export const SendEventOnView = <E extends AnalyticsEvent>({
    event,
    id,
    sendOnce = true,
    trigger = "view",
}: {
    event: E
    id: string
    sendOnce?: boolean
    /**
     * `view` (padrão): quando o elemento entra no viewport.
     * `render`: assim que o elemento existe na página — use para a lista
     * principal da página (PLP/busca).
     */
    trigger?: ViewTrigger
}) => {
    const payload = JSON.stringify(
        trigger === "render" ? [id, event, sendOnce, trigger] : [id, event, sendOnce],
    )
    useRegisterOnClient("__decoRegisterView", "__decoPendingViews", payload)
    return (
        <script
            {...inlineScript(
                registerOrQueue("__decoRegisterView", "__decoPendingViews", payload),
            )}
        />
    )
}
